import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { forkJoin } from 'rxjs';
import { DashboardPin } from '../../../models/dashboard-pin.model';
import { ReleaseAssessment, ReleaseRecommendation } from '../../../models/release-assessment.model';
import { ReleaseResultsSummary } from '../../../models/release-results.model';
import { ReleaseService } from '../../../services/release.service';
import { ReleaseResultsService } from '../../../services/release-results.service';
import { environmentFilterLabel } from '../../../shared/branch-name';

type KachelState = 'loading' | 'loaded' | 'error';

interface KachelData {
  assessment: ReleaseAssessment;
  summary: ReleaseResultsSummary;
}

@Component({
  selector: 'app-freigabe-kachel',
  imports: [TranslatePipe, MatIcon, MatTooltip],
  templateUrl: './freigabe-kachel.component.html',
  styleUrl: './freigabe-kachel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FreigabeKachelComponent implements OnInit {
  private releaseService = inject(ReleaseService);
  private resultsService = inject(ReleaseResultsService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  readonly pin = input.required<DashboardPin>();
  readonly showProject = input(false);
  readonly remove = output<DashboardPin>();

  protected readonly state = signal<KachelState>('loading');
  private readonly data = signal<KachelData | null>(null);

  protected readonly environmentLabel = computed(() => {
    const environment = this.pin().environment;
    return environment ? environmentFilterLabel(environment) : null;
  });
  protected readonly ampel = computed<ReleaseRecommendation | null>(() => this.data()?.assessment.empfehlung ?? null);
  protected readonly planCount = computed(() => this.data()?.summary.planCount ?? null);
  protected readonly passRate = computed(() => this.data()?.summary.passRate ?? 0);
  protected readonly executed = computed(() => {
    const summary = this.data()?.summary;
    return summary && summary.gesamt > 0 ? Math.round((summary.ausgefuehrt / summary.gesamt) * 100) : 0;
  });
  protected readonly blockerCount = computed(() => this.data()?.summary.blocker.length ?? 0);
  protected readonly criticalCount = computed(
    () => this.data()?.summary.blocker.filter((b) => b.prio === 'CRITICAL').length ?? 0,
  );

  ngOnInit(): void {
    const { projectId, releaseId, environment } = this.pin();
    forkJoin({
      assessment: this.releaseService.getAssessment(projectId, releaseId),
      summary: this.resultsService.getSummary(projectId, releaseId, environment ? { environment } : {}),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.data.set(data);
          this.state.set('loaded');
        },
        error: () => this.state.set('error'),
      });
  }

  protected open(): void {
    const { projectId, releaseId, environment } = this.pin();
    const queryParams: Record<string, string> = { release: releaseId };
    if (environment) queryParams['umgebung'] = environment;
    void this.router.navigate(['/projects', projectId, 'test-results'], { queryParams });
  }

  protected onRemove(event: Event): void {
    event.stopPropagation();
    this.remove.emit(this.pin());
  }
}
